import { z } from "zod";

import type { IProduct, IProductTax } from "@/api/interfaces/productLayouts";
import type { UnifiedCartLine } from "@/store/shoppingCart/types";

import type { IDiningOptionEnum } from "./configurationLayouts";

export type ICartModifierChoices = {
  ProductModifierChoiceID: number;
  ProductModifierID: number;
  RelatedProductID: number;
  ProductModifierChoiceDisplayName: string;
  ProductModifierChoicePublicDescription: string;
  RelatedProductShoppingCartLineID: number;
  Quantity: number;
};

export type IRelatedChildShoppingCartLines = {
  ID: number;
};

export type TheatreResource = {
  ID: number;
  Name: string;
};

export type ICartLineItem = {
  ID: number;
  Name: string;
  Amount: number;
  GrossSum: number;
  GrossUnitPrice: number;
  NetSum: number;
  NetUnitPrice: number;
  CustomModifiers: string;
  Product: IProduct;
  Tax: IProductTax;
  ProductModifierChoices?: ICartModifierChoices[];
  RelatedShoppingCartLineID: number;
  RelatedChildShoppingCartLines?: IRelatedChildShoppingCartLines[];
  VariableProductID?: number;
  ProductPackagingSetConstraintID?: number;
  groupedIds?: number[];
  TheatreResource: TheatreResource;
};

export type ICartTax = {
  Sum: number;
  Tax: IProductTax;
};

export type ISum = {
  LineNetSum: number;
  LineGrossSum: number;
  Taxes: ICartTax[];
  LineTotalAmount: number;
};

export type PurchaseConfirmation = {
  Category: string;
  Email: string;
  Address: string;
};

export type CustomerPerson = {
  ID: number;
  FirstName: string;
  LastName: string;
  Status: number;
  Email: string;
  Mobile: string;
};

export type ICartLayout = {
  ID: number;
  BarcodeWithoutChecksum: string;
  ContactInfo: ICartContactInfo | null;
  Defaults?: {
    DiningOptionID: IDiningOptionEnum;
    TheatreResourceID: number;
    dttmRequestedDelivery: string | null;
  };
  Status: CartStatus;
  Lines?: UnifiedCartLine[];
  Sums: ISum;
  PurchaseConfirmation: PurchaseConfirmation;
  CustomerPerson: CustomerPerson;
  dttmCreated: Date;
  dttmCreatedUTC: Date;
  dttmExpires: Date;
  dttmExpiresUTC: Date;
  dttmNowUTC: Date;
  CheckoutOptions: ICheckoutOption[];
};

export enum EShoppingCartOnHoldReason {
  Draft = 2,
  Locked = 3,
  OpenInvoice = 4,
}

export enum ECheckoutOptionType {
  PayNow = 1,
  PayLater = 2,
}

export const ICustomFieldsAndValuesSchema = z.object({
  CustomFieldValueId: z.number().optional(),
  CustomFieldId: z.number().optional(),
  CustomFieldName: z.string().optional(),
  ObjectKeyId: z.number().optional(),
  ObjectInstanceId: z.number().optional(),
  CustomFieldDataTypeId: z.number().optional(),
  CustomFieldStatusId: z.number().optional(),
  CustomFieldValueStatusId: z.number().optional(),
  ObjectInstanceSubitemId: z.number().optional(),
  IsTranslatable: z.boolean().optional(),
  Preference: z.number().optional(),
  TextValue: z.string().optional(),
});

export type ICustomFieldsAndValues = {
  CustomFieldValueId?: number;
  CustomFieldId?: number;
  CustomFieldName?: string;
  ObjectKeyId?: number;
  ObjectInstanceId?: number;
  CustomFieldDataTypeId?: number;
  CustomFieldStatusId?: number;
  CustomFieldValueStatusId?: number;
  ObjectInstanceSubitemId?: number;
  IsTranslatable?: boolean;
  Preference?: number;
  TextValue?: string;
};

export const ICheckoutOptionSchema = z.object({
  ShoppingCartOnHoldReason: z.nativeEnum(EShoppingCartOnHoldReason),
  CheckoutOptionType: z.nativeEnum(ECheckoutOptionType),
  IsEnabled: z.boolean(),
  Title: z.string().optional(),
  CustomFieldsAndValues: z.array(ICustomFieldsAndValuesSchema).nullable(),
});
export type ICheckoutOption = z.infer<typeof ICheckoutOptionSchema>;

export type ICartAddModifierChoice = {
  ProductModifierID: number;
  ProductModifierChoiceID: number;
  Amount: number;
  ProductId?: number;
};

export type ILineAdd = {
  ProductID: number;
  Amount: number;
  ModifierChoices?: ICartAddModifierChoice[];
  CustomModifiers?: string;
  VariableProductID?: number;
};

export type IAddLineToCartBody = {
  Operation: number;
  Products: ILineAdd[];
};

export type AddProductToCartRequest = {
  transactionId: number;
  body: IAddLineToCartBody;
};

export type ISetCartCustomer = {
  CustomerPersonID: number;
  LoyaltyProgramMembershipID: number;
};

export enum CartStatus {
  UNKNOWN = 0,
  REGULAR = 1,
  ON_HOLD = 2,
}

export const ICartContactInfoSchema = z.object({
  Name: z.string().nullable(),
  Phone: z.string().nullable(),
  Email: z.string().nullable(),
});

export type ICartContactInfo = z.infer<typeof ICartContactInfoSchema>;

export type GetShoppingCartByBarcodeRequest = {
  transactionId: number;
};

export type RemoveProductFromCartRequest = {
  productId: number;
  transactionId: number;
};

export type RemoveAllProductsFromCartRequest = {
  transactionId: number;
};

export type RemoveLineFromCartRequest = {
  cartLineIds: number[];
  transactionId: number;
};

export type BundleRequest = {
  transactionId: number;
};

export type UpdateCustomerCartRequest = {
  transactionId: number;
  body: ISetCartCustomer;
};

export type UnbundleRequest = {
  transactionId: number;
};

export type UpdateTheatreResourceRequest = {
  theatreResourceId: number;
  transactionId: number;
};

export type RemoveCustomerFromCartRequest = {
  transactionId: number;
};

export type BindContactInfoRequest = {
  transactionId: number;
  body: ICartContactInfo;
};

export type InitializeDiningCartRequest = {
  theatreResourceId: number;
  dttmRequestedDelivery: string | null;
  diningOptionId: IDiningOptionEnum;
  customerPersonId?: number;
};

export type UpdateLanguageRequest = {
  transactionId: number;
  body: { language: string };
};

export type PutOnHoldRequest = {
  transactionId: number;
  body: {
    ShoppingCartOnHoldReason: EShoppingCartOnHoldReason;
    Description: string;
    StatusMonitorUrl: string;
  };
};

export type DeleteShoppingCartRequest = {
  transactionId: number;
};
